import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@scio3/ui';
import type { AdminUser } from '@scio3/shared-types';
import { STAFF_DISABLE_ADMIN_USER } from '@scio3/graphql-client';
import { useDialogSubmitState } from '@/hooks/useDialogSubmitState';

interface Props {
  user: AdminUser | null;
  onOpenChange: (open: boolean) => void;
}

interface DisableResult {
  staffDisableAdminUser: AdminUser;
}

/**
 * Destructive disable dialog with typed-email confirmation.
 * Matches the pattern used for high-impact destructive actions in apps/web.
 */
export function DisableUserDialog({ user, onOpenChange }: Props) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState('');

  useEffect(() => {
    if (!user) setTyped('');
  }, [user]);

  const [disableMutation, { loading }] = useMutation<DisableResult>(STAFF_DISABLE_ADMIN_USER, {
    refetchQueries: ['staffAdminUsers'],
  });

  const confirmed = !!user && typed.trim() === user.email;
  const { disabled: submitDisabled, disabledTooltip } = useDialogSubmitState({
    isSubmitting: loading,
    isConfirmed: confirmed,
  });

  const handleSubmit = async () => {
    if (!user) return;
    try {
      await disableMutation({ variables: { email: user.email } });
      toast.success(t('User disabled'));
      onOpenChange(false);
    } catch (err) {
      const msg = (err as { message?: string })?.message ?? t('Unknown error');
      toast.error(t('Disable failed'), { description: msg });
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent data-testid="disable-user-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('Disable user')}</DialogTitle>
        </DialogHeader>

        {user ? (
          <div className="flex flex-col gap-4 py-2">
            <p className="text-sm text-muted-foreground">
              {t(
                'This user will lose access on their next request (within the 5-second guard cache). They can be re-enabled later.',
              )}
            </p>
            <div className="space-y-2">
              <Label htmlFor="disable-confirm">
                {t('Type the email to confirm:')}{' '}
                <span className="font-mono font-semibold">{user.email}</span>
              </Label>
              <Input
                id="disable-confirm"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={user.email}
                data-testid="disable-confirm-input"
                autoComplete="off"
              />
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">{t('Cancel')}</Button>
          </DialogClose>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={submitDisabled ? 0 : -1}>
                  <Button
                    variant="destructive"
                    onClick={handleSubmit}
                    disabled={submitDisabled}
                    data-testid="disable-user-submit"
                  >
                    {loading ? t('Disabling…') : t('Disable')}
                  </Button>
                </span>
              </TooltipTrigger>
              {disabledTooltip ? <TooltipContent>{disabledTooltip}</TooltipContent> : null}
            </Tooltip>
          </TooltipProvider>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
